import { useEffect, useRef, useState } from "react";
import type { Placement, TourStep } from "./tourSteps";

type Rect = { top: number; left: number; width: number; height: number };

type Props = {
  step: TourStep;
  stepIndex: number;
  stepCount: number;
  rect: Rect | null;
  paused: boolean;
  onPauseToggle: () => void;
  onSkip: () => void;
  onDone: () => void;
};

const GAP = 18;
const SAFE = 12;

function resolvePlacement(
  preferred: Placement,
  rect: Rect | null,
  tw: number,
  th: number
): Placement {
  if (!rect || preferred === "center") return preferred;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (preferred === "bottom" && rect.top + rect.height + GAP + th + SAFE > vh) return "top";
  if (preferred === "top" && rect.top - GAP - th - SAFE < 0) return "bottom";
  if (preferred === "right" && rect.left + rect.width + GAP + tw + SAFE > vw) return "left";
  if (preferred === "left" && rect.left - GAP - tw - SAFE < 0) return "right";
  return preferred;
}

function computePosition(placement: Placement, rect: Rect | null, tw: number, th: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect || placement === "center") {
    return { top: Math.round((vh - th) / 2), left: Math.round((vw - tw) / 2) };
  }
  let top = 0;
  let left = 0;
  if (placement === "bottom") {
    top = rect.top + rect.height + GAP;
    left = rect.left + rect.width / 2 - tw / 2;
  } else if (placement === "top") {
    top = rect.top - GAP - th;
    left = rect.left + rect.width / 2 - tw / 2;
  } else if (placement === "right") {
    top = rect.top + rect.height / 2 - th / 2;
    left = rect.left + rect.width + GAP;
  } else if (placement === "left") {
    top = rect.top + rect.height / 2 - th / 2;
    left = rect.left - GAP - tw;
  }
  left = Math.max(SAFE, Math.min(left, vw - tw - SAFE));
  top = Math.max(SAFE, Math.min(top, vh - th - SAFE));
  return { top: Math.round(top), left: Math.round(left) };
}

export function TourTooltip({
  step,
  stepIndex,
  stepCount,
  rect,
  paused,
  onPauseToggle,
  onSkip,
  onDone,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 320, h: 160 });

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [step]);

  const preferred = step.placement ?? "bottom";
  const placement = resolvePlacement(preferred, rect, size.w, size.h);
  const { top, left } = computePosition(placement, rect, size.w, size.h);
  const isLast = stepIndex === stepCount - 1;

  return (
    <div
      ref={ref}
      className="tour-tooltip"
      style={{
        position: "fixed",
        top,
        left,
        zIndex: 9300,
      }}
      role="dialog"
      aria-label={step.heading}
    >
      <div className="tour-tooltip-eb">
        {step.eyebrow ?? `STEP ${stepIndex + 1} / ${stepCount}`}
      </div>
      <div className="tour-tooltip-heading">{step.heading}</div>
      <div className="tour-tooltip-body">{step.body}</div>
      <div className="tour-tooltip-progress" aria-hidden="true">
        {Array.from({ length: stepCount }).map((_, i) => (
          <span
            key={i}
            className={"tour-dot" + (i === stepIndex ? " active" : i < stepIndex ? " done" : "")}
          />
        ))}
      </div>
      <div className="tour-tooltip-actions">
        <span className="tour-tooltip-counter">
          {stepIndex + 1} / {stepCount}
        </span>
        <div className="tour-tooltip-actions-right">
          {!isLast && (
            <button type="button" className="tour-btn tour-btn-ghost" onClick={onSkip}>
              Skip
            </button>
          )}
          {!isLast && (
            <button type="button" className="tour-btn tour-btn-ghost" onClick={onPauseToggle}>
              {paused ? "Resume" : "Pause"}
            </button>
          )}
          <button type="button" className="tour-btn tour-btn-primary" onClick={onDone}>
            {isLast ? "Done" : "Next ›"}
          </button>
        </div>
      </div>
    </div>
  );
}
